"use client"

import React, { useState, useMemo, useEffect } from "react"
import { FiPlus, FiTrash2, FiEdit } from "react-icons/fi"
import CreateMatchCategoryOffcanvas from "../../components/match-categories/create-match-category-offcanvas"
import EditMatchCategoryOffcanvas from "../../components/match-categories/edit-match-category-offcanvas"
import { ListLoader } from "../../utils/loader"
import Breadcrumb from "../../utils/breadcrumbs"
import ConfirmationModal from "../../ui/confirmation-modal"
import Pagination from "../../utils/pagination2"
import toast from "react-hot-toast"
import api from "../../libs/axios"

export interface IMatchCategory {
    id: number
    name: string
    description: string
    status: string
    created_at?: Date
    updated_at?: Date
}

const MatchCategories: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<IMatchCategory | null>(null)
    const [searchParams, setSearchParams] = useState<Record<string, string>>({})
    const [showAddCategoryCanvas, setShowAddCategoryCanvas] = useState(false)
    const [showEditCategoryCanvas, setShowEditCategoryCanvas] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [categories, setCategories] = useState<IMatchCategory[]>([])
    const [loading, setLoading] = useState(false)

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })

    // Fetch categories from API
    const fetchCategories = async () => {
        setLoading(true)
        try {
            // Build query parameters
            const queryParams = new URLSearchParams({
                page: currentPage.toString(),
                limit: limit.toString(),
                ...searchParams
            })

            const response = await api.get(`/match-categories?${queryParams.toString()}`)
            setCategories(response.data.data.categories || response.data.data)

            // Handle pagination data from response
            if (response.data.pagination) {
                setPagination(response.data.pagination)
            } else {
                // If no pagination in response, create basic pagination
                setPagination({
                    current_page: response.data.data.current_page || currentPage,
                    per_page: response.data.data.per_page || limit,
                    total: response.data.data.total || response.data.data.count || 0,
                    total_pages: response.data.data.total_pages || Math.ceil((response.data.data.total || response.data.data.count || 0) / limit),
                    has_next_page: response.data.data.has_next_page || false,
                    has_prev_page: response.data.data.has_prev_page || false,
                    next_page: response.data.data.next_page || null,
                    prev_page: response.data.data.prev_page || null,
                })
            }
        } catch (error: any) {
            console.error("Error fetching match categories:", error)
            toast.error("Failed to fetch match categories")
        } finally {
            setLoading(false)
        }
    }

    // Filter categories using useMemo (client-side filtering for better UX)
    const filteredCategories = useMemo(() => {
        return categories.filter((category) => {
            const nameMatch = !searchParams.name || category.name.toLowerCase().includes(searchParams.name.toLowerCase())

            const descriptionMatch =
                !searchParams.description || category.description.toLowerCase().includes(searchParams.description.toLowerCase())

            const statusMatch = !searchParams.status || category.status.toLowerCase() === searchParams.status.toLowerCase()

            return nameMatch && descriptionMatch && statusMatch
        })
    }, [categories, searchParams])

    // Fetch categories on component mount and when filters change
    useEffect(() => {
        fetchCategories()
    }, [currentPage, limit])

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1) // Reset to first page when searching
            } else {
                fetchCategories()
            }
        }, 500)

        return () => clearTimeout(timer)
    }, [searchParams])

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev }
            if (value) {
                newParams[column] = value
            } else {
                delete newParams[column]
            }
            return newParams
        })
    }

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return "bg-green-100 text-green-800"
            case "inactive":
                return "bg-red-100 text-red-800"
            case "pending":
                return "bg-yellow-100 text-yellow-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        setPagination(prev => ({
            ...prev,
            current_page: page
        }))
    }

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit)
        setCurrentPage(1)
        setPagination(prev => ({
            ...prev,
            per_page: newLimit,
            current_page: 1
        }))
    }

    const handleEditClick = (category: IMatchCategory, e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedCategory(category)
        setShowEditCategoryCanvas(true)
    }

    // Handle delete
    const handleDeleteClick = (category: IMatchCategory, e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedCategory(category)
        setDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (selectedCategory) {
            try {
                await api.delete(`/match-categories/${selectedCategory.id}`)
                setCategories(categories.filter((category) => category.id !== selectedCategory.id))
                toast.success(`${selectedCategory.name} deleted successfully!`)
                fetchCategories() // Refresh the data
            } catch (err) {
                console.error('Error deleting match category:', err)
                toast.error("Failed to delete match category")
            }
            setDeleteConfirmOpen(false)
            setSelectedCategory(null)
        }
    }

    const handleCategoryCreated = () => {
        fetchCategories() // Refresh the list
    }

    const handleCategoryUpdated = () => {
        fetchCategories() // Refresh the list
        setShowEditCategoryCanvas(false)
        setSelectedCategory(null)
    }

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Match Categories
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={() => setShowAddCategoryCanvas(true)}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Category</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Category Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Description
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Category Name"
                                        value={searchParams.name || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("name", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Description"
                                        value={searchParams.description || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("description", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("status", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredCategories.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-8 text-gray-500">
                                        No match categories found
                                    </td>
                                </tr>
                            ) : (
                                filteredCategories.map((category) => (
                                    <tr
                                        key={category.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="font-semibold">{category.name}</div>
                                        </td>
                                        <td className="px-6 py-4 border-r">
                                            <div className="text-sm text-gray-600">{category.description}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(category.status)}`}>
                                                {category.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={(e) => handleEditClick(category, e)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Edit Category"
                                                >
                                                    <FiEdit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteClick(category, e)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete Category"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                <CreateMatchCategoryOffcanvas
                    showCreateCategoryCanvas={showAddCategoryCanvas}
                    setShowCreateCategoryCanvas={setShowAddCategoryCanvas}
                    onCategoryCreated={handleCategoryCreated}
                />

                {selectedCategory && (
                    <EditMatchCategoryOffcanvas
                        categoryId={selectedCategory.id.toString()}
                        showEditCategoryCanvas={showEditCategoryCanvas}
                        setShowEditCategoryCanvas={setShowEditCategoryCanvas}
                        onCategoryUpdated={handleCategoryUpdated}
                    />
                )}

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Match Category"
                        description="Are you sure you want to delete this match category? This action cannot be undone."
                        loading={loading}
                    />
                )}
            </div>
        </div>
    )
}

export default MatchCategories